import { useState, useRef } from 'react';
import { mediaService } from '../../services/media';
import { useMedia } from '../../hooks/useMedia';
import { Upload, X, Loader2, Image as ImageIcon, FolderOpen, Check, HardDrive, Layers } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import type { Media, ResponsiveImageVersion } from '../../types/entities';

interface MediaPickerProps {
  value?: string | null;
  onChange: (url: string | null, alt?: string | null) => void;
  label?: string;
}

export function MediaPicker({ value, onChange, label = 'Image' }: MediaPickerProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [galleryPage, setGalleryPage] = useState(1);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadAlt, setUploadAlt] = useState('');
  const [selectedMediaAlt, setSelectedMediaAlt] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: mediaData, isLoading: isLoadingMedia, refetch } = useMedia(galleryPage, 12);

  // Generate suggested title from filename
  const generateSuggestedTitle = (filename: string): string => {
    return filename
      .replace(/\.[^/.]+$/, '') // Remove extension
      .replace(/[-_]/g, ' ')    // Replace dashes/underscores with spaces
      .replace(/\s+/g, ' ')     // Normalize spaces
      .trim();
  };

  const handleFileSelected = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('L\'image ne doit pas dépasser 5MB');
      return;
    }

    // Open dialog to enter title and alt
    setPendingFile(file);
    setUploadTitle(generateSuggestedTitle(file.name));
    setUploadAlt('');
    setIsUploadDialogOpen(true);
  };

  const handleUploadConfirm = async () => {
    if (!pendingFile) return;

    setIsUploadDialogOpen(false);
    setIsUploading(true);
    setError(null);
    setProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const media = await mediaService.uploadImage(pendingFile, {
        title: uploadTitle || undefined,
        alt: uploadAlt || undefined,
      });

      clearInterval(progressInterval);
      setProgress(100);
      onChange(media.cdnUrl, media.alt);
      refetch(); // Refresh gallery
    } catch (err) {
      console.error('Upload error:', err);
      setError('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      setProgress(0);
      setPendingFile(null);
      setUploadTitle('');
      setUploadAlt('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
    e.target.value = ''; // Reset input
  };

  const handleRemove = () => {
    onChange(null);
    setSelectedMediaAlt('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSelectFromGallery = (media: Media) => {
    onChange(media.cdnUrl, media.alt);
    setSelectedMediaAlt(media.alt || '');
    setIsGalleryOpen(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const parseResponsiveUrls = (responsiveUrls: string | null): ResponsiveImageVersion[] => {
    if (!responsiveUrls) return [];
    try {
      return JSON.parse(responsiveUrls);
    } catch {
      return [];
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>

      {value ? (
        // Preview with selected image
        <div className="space-y-2">
          <div className="relative">
            <img
              src={value}
              alt={selectedMediaAlt || 'Preview'}
              className="w-full h-48 object-cover rounded-md border border-gray-200"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">Erreur</text></svg>';
              }}
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsGalleryOpen(true)}
                title="Changer l'image"
              >
                <FolderOpen className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRemove}
                title="Supprimer"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Alt text display/hint */}
          {selectedMediaAlt && (
            <p className="text-xs text-gray-500">
              <strong>Alt:</strong> {selectedMediaAlt}
            </p>
          )}
        </div>
      ) : (
        // Upload area + Gallery button
        <div className="space-y-3">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors
              ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
              ${isUploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            {isUploading ? (
              <div className="space-y-3">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">Upload en cours...</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-500 h-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-8 h-8 mx-auto text-gray-400" />
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-blue-600">Cliquez pour uploader</span>
                  {' ou glissez-déposez'}
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, WEBP jusqu'à 5MB</p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-500 uppercase">ou</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={() => setIsGalleryOpen(true)}
          >
            <FolderOpen className="w-4 h-4 flex-shrink-0" />
            <span className="sm:hidden">Galerie</span>
            <span className="hidden sm:inline whitespace-nowrap">Choisir depuis la galerie</span>
          </Button>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Upload Dialog - Enter title and alt */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Informations de l'image</DialogTitle>
            <DialogDescription>
              Renseignez le titre et le texte alternatif pour le référencement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {pendingFile && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <ImageIcon className="w-8 h-8 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {pendingFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(pendingFile.size)}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="upload-title">
                Nom de l'image (SEO)
                <span className="text-gray-400 font-normal ml-1">- utilisé pour le nom du fichier</span>
              </Label>
              <Input
                id="upload-title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Ex: massage-relaxant-lourdes"
              />
              <p className="text-xs text-gray-500">
                Sera formaté automatiquement (minuscules, tirets)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-alt">
                Texte alternatif (Alt)
                <span className="text-gray-400 font-normal ml-1">- accessibilité & SEO</span>
              </Label>
              <Textarea
                id="upload-alt"
                value={uploadAlt}
                onChange={(e) => setUploadAlt(e.target.value)}
                placeholder="Ex: Massage relaxant dans un cadre apaisant à Lourdes"
                rows={2}
              />
              <p className="text-xs text-gray-500">
                Décrivez l'image pour les lecteurs d'écran et le référencement
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsUploadDialogOpen(false);
                setPendingFile(null);
              }}
            >
              Annuler
            </Button>
            <Button type="button" onClick={handleUploadConfirm}>
              <Upload className="w-4 h-4 mr-2" />
              Uploader
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gallery Modal */}
      <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Bibliothèque Média</DialogTitle>
            <DialogDescription>
              Sélectionnez une image dans la galerie.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {isLoadingMedia && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} className="aspect-square w-full" />
                ))}
              </div>
            )}

            {mediaData && mediaData.data.length === 0 && (
              <div className="text-center py-12">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun média</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Uploadez d'abord des images
                </p>
              </div>
            )}

            {mediaData && mediaData.data.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {mediaData.data.map((media: Media) => {
                  const isSelected = value === media.cdnUrl;
                  const versions = parseResponsiveUrls(media.responsiveUrls);

                  return (
                    <button
                      key={media.id}
                      type="button"
                      onClick={() => handleSelectFromGallery(media)}
                      className={`
                        relative aspect-square rounded-lg overflow-hidden border-2 transition-all text-left
                        ${isSelected
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-transparent hover:border-gray-300'
                        }
                      `}
                    >
                      <img
                        src={media.thumbnailUrl || media.cdnUrl}
                        alt={media.alt || media.filename}
                        className="w-full h-full object-cover"
                      />

                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}

                      {/* Info overlay on hover */}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 hover:opacity-100 transition-opacity">
                        <p className="text-xs text-white truncate font-medium">
                          {media.title || media.filename}
                        </p>
                        {media.alt && (
                          <p className="text-xs text-gray-300 truncate">
                            {media.alt}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-300 mt-1">
                          <span className="flex items-center gap-1">
                            <HardDrive className="h-3 w-3" />
                            {formatFileSize(media.size)}
                          </span>
                          {versions.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Layers className="h-3 w-3" />
                              {versions.length}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          {mediaData && mediaData.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setGalleryPage(p => Math.max(1, p - 1))}
                disabled={galleryPage === 1}
              >
                Précédent
              </Button>
              <span className="text-sm text-gray-600">
                {galleryPage} / {mediaData.pagination.totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setGalleryPage(p => Math.min(mediaData.pagination.totalPages, p + 1))}
                disabled={galleryPage === mediaData.pagination.totalPages}
              >
                Suivant
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
